// src/pages/PendingOrders/PendingOrders.jsx - WITH SELL LOCK RETRY INTEGRATION
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tradingService } from '../../services/tradingService';
import { copyTradingService } from '../../services/copyTradingService';
import { useCurrency } from '../../context/CurrencyContext';
import { useSellLockRetry } from '../../hooks/useSellLockRetry';
import './PendingOrders.css';

/* ── Small inline logo loader — used for page-level loading states ── */
const MiniLogoLoader = ({ label }) => (
  <div className="mini-loader-wrap">
    <div className="mini-logo">
      <div className="mini-bar-wrap mini-bar-left">
        <div className="mini-bar-fill mini-fill-left" />
      </div>
      <div className="mini-bar-wrap mini-bar-center">
        <div className="mini-bar-fill mini-fill-center" />
      </div>
      <div className="mini-bar-wrap mini-bar-right">
        <div className="mini-bar-fill mini-fill-right" />
      </div>
    </div>
    {label && <p className="mini-loader-label">{label}</p>}
  </div>
);

const PendingOrders = () => {
  const navigate = useNavigate();
  const { currency, formatCurrency, getCurrencySymbol } = useCurrency();
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pendingCopyTrades, setPendingCopyTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [success, setSuccess] = useState(null);

  // Use the sell lock retry hook
  const { loading: sellLockLoading, attemptAction } = useSellLockRetry();

  useEffect(() => {
    fetchPendingOrders();
  }, []);

  // Refresh when currency changes
  useEffect(() => {
    const handleCurrencyChange = () => {
      fetchPendingOrders();
    };

    window.addEventListener('currencyChange', handleCurrencyChange);
    return () => window.removeEventListener('currencyChange', handleCurrencyChange);
  }, []);

  const fetchPendingOrders = async () => {
    try {
      setLoading(true);

      // Fetch own pending limit orders AND pending copy trades in parallel.
      // Pending copy trades (status='PENDING') represent funds the system has
      // reserved from the copier's wallet while waiting for the leader's limit
      // order to fill — they are real reservations the user should be aware of.
      const [orders, copyTrades] = await Promise.all([
        tradingService.getUserOrders({ status: 'PENDING' }),
        copyTradingService.getMyCopyTrades(),
      ]);

      console.log('📋 Fetched pending orders:', orders);
      console.log('📋 Fetched copy trades:', copyTrades);

      setPendingOrders(orders);
      setPendingCopyTrades(
        Array.isArray(copyTrades)
          ? copyTrades.filter(ct => ct.status === 'PENDING')
          : []
      );
    } catch (err) {
      setError(err.message || 'Failed to fetch pending orders');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToMarket = async (orderId) => {
    if (!window.confirm('Are you sure you want to execute this order at the current market price?')) {
      return;
    }

    // Use the sell lock retry pattern
    attemptAction(
      () => tradingService.convertToMarketOrder(orderId),
      (response) => {
        setSuccess(response.message || 'Order executed successfully at market price!');
        fetchPendingOrders();
      },
      (err) => {
        setError(err.message || 'Failed to convert order');
      },
      orderId
    );
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }

    try {
      setActionLoading(orderId);
      setError(null);
      setSuccess(null);

      await tradingService.cancelOrder(orderId);
      setSuccess('Order cancelled successfully');

      await fetchPendingOrders();
    } catch (err) {
      setError(err.message || 'Failed to cancel order');
    } finally {
      setActionLoading(null);
    }
  };

  const calculatePriceDifference = (order) => {
    const currentPrice = parseFloat(order.stock.current_price_display || order.stock.current_price);
    const limitPrice = parseFloat(order.limit_price);
    const difference = currentPrice - limitPrice;
    const percentDiff = limitPrice !== 0 ? ((difference / limitPrice) * 100).toFixed(2) : '0.00';

    console.log('💰 Price calc:', {
      symbol: order.stock.symbol,
      currentPrice,
      limitPrice,
      difference,
      percentDiff,
      currency: order.display_currency,
    });

    return {
      difference: difference.toFixed(2),
      percentDiff,
      isReached: order.order_side === 'BUY' ? currentPrice <= limitPrice : currentPrice >= limitPrice,
    };
  };

  const getOrderStatusBadge = (order) => {
    const priceDiff = calculatePriceDifference(order);

    if (priceDiff.isReached) {
      return <span className="status-badge reached">Price Reached</span>;
    } else {
      return <span className="status-badge waiting">Waiting</span>;
    }
  };

  // Helper to format currency consistently
  const formatPrice = (value) => {
    if (!value) return formatCurrency(0);
    return formatCurrency(parseFloat(value));
  };

  // Check if a specific order is currently loading (from sell lock retry)
  const isOrderLoading = (orderId) => {
    return actionLoading === orderId || sellLockLoading === orderId;
  };

  const hasAnything = pendingOrders.length > 0 || pendingCopyTrades.length > 0;

  if (loading) {
    return (
      <div className="pending-orders-container">
        <div className="loading">
          <MiniLogoLoader label="Loading pending orders..." />
        </div>
      </div>
    );
  }

  return (
    <div className="pending-orders-container">
      {/* Header */}
      <div className="pending-orders-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Pending Orders</h1>
        <p>Manage your limit orders - convert to market or cancel</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="message error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {success && (
        <div className="message success-message">
          {success}
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Empty state — only when both lists are empty */}
      {!hasAnything ? (
        <div className="no-orders">
          <div className="no-orders-icon">📋</div>
          <h2>No Pending Orders</h2>
          <p>You don't have any pending limit orders at the moment.</p>
          <button className="explore-button" onClick={() => navigate('/stocks')}>
            Explore Stocks
          </button>
        </div>
      ) : (
        <>
          {/* ── Your own pending limit orders ── */}
          {pendingOrders.length > 0 && (
            <div className="orders-list">
              {pendingOrders.map(order => {
                const priceDiff = calculatePriceDifference(order);
                const isBuy = order.order_side === 'BUY';
                const isLoading = isOrderLoading(order.id);

                return (
                  <div key={order.id} className={`order-card ${isBuy ? 'buy' : 'sell'}`}>
                    {/* Order Header */}
                    <div className="order-header">
                      <div className="stock-info">
                        <h3>{order.stock.symbol}</h3>
                        <span className="stock-name">{order.stock.name}</span>
                      </div>
                      {getOrderStatusBadge(order)}
                    </div>

                    {/* Order Details */}
                    <div className="order-details">
                      <div className="detail-row">
                        <span className="label">Type:</span>
                        <span className={`value order-type ${isBuy ? 'buy' : 'sell'}`}>
                          {order.order_side} Limit Order
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="label">Quantity:</span>
                        <span className="value">{order.quantity} shares</span>
                      </div>

                      <div className="detail-row">
                        <span className="label">Your Limit Price:</span>
                        <span className="value highlight">
                          {order.limit_price_display || formatPrice(order.limit_price)}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="label">Current Market Price:</span>
                        <span className="value">
                          {order.stock.formatted_price ||
                            formatPrice(order.stock.current_price_display || order.stock.current_price)}
                        </span>
                      </div>

                      <div className="detail-row price-difference">
                        <span className="label">Price Difference:</span>
                        <span
                          className={`value ${
                            parseFloat(priceDiff.difference) >= 0 ? 'positive' : 'negative'
                          }`}
                        >
                          {parseFloat(priceDiff.difference) >= 0 ? '+' : ''}
                          {formatPrice(Math.abs(parseFloat(priceDiff.difference)))}
                          {' '}({parseFloat(priceDiff.percentDiff) >= 0 ? '+' : ''}
                          {priceDiff.percentDiff}%)
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="label">Total Amount:</span>
                        <span className="value bold">
                          {order.total_amount_display || formatPrice(order.total_amount)}
                        </span>
                      </div>

                      {order.use_referral_bonus && (
                        <div className="detail-row">
                          <span className="label">Payment:</span>
                          <span className="value referral-badge">Using Referral Bonus</span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="label">Created:</span>
                        <span className="value date">
                          {new Date(order.created_at).toLocaleString()}
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="label">Currency:</span>
                        <span className="value">{order.display_currency || currency}</span>
                      </div>
                    </div>

                    {/* Explanation */}
                    <div className="order-explanation">
                      <strong>What happens next?</strong>
                      <p>
                        {isBuy ? (
                          <>
                            This order will execute when the stock price drops to or below{' '}
                            {order.limit_price_display || formatPrice(order.limit_price)}.{' '}
                            Current price needs to decrease by{' '}
                            {formatPrice(Math.abs(parseFloat(priceDiff.difference)))}.
                          </>
                        ) : (
                          <>
                            This order will execute when the stock price rises to or above{' '}
                            {order.limit_price_display || formatPrice(order.limit_price)}.{' '}
                            Current price needs to increase by{' '}
                            {formatPrice(Math.abs(parseFloat(priceDiff.difference)))}.
                          </>
                        )}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="order-actions">
                      <button
                        className={`action-button convert ${isBuy ? 'buy' : 'sell'}`}
                        onClick={() => handleConvertToMarket(order.id)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          'Processing...'
                        ) : (
                          <>
                            Execute Now at Market Price (
                            {order.stock.formatted_price ||
                              formatPrice(
                                order.stock.current_price_display || order.stock.current_price
                              )}
                            )
                          </>
                        )}
                      </button>

                      <button
                        className="action-button cancel"
                        onClick={() => handleCancelOrder(order.id)}
                        disabled={isLoading}
                      >
                        Cancel Order
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Pending copy trades (funds reserved while leader's limit order waits) ── */}
          {pendingCopyTrades.length > 0 && (
            <div className="orders-list">
              <div className="section-divider">
                <h2 className="section-title">Reserved Copy Trades</h2>
                <p className="section-subtitle">
                  Orders placed on your behalf while waiting for your leader's limit
                  order to fill. Reservations are released automatically if the leader cancels.
                </p>
              </div>

              {pendingCopyTrades.map(ct => {
                const isCopyBuy = ct.order_side === 'BUY';
                return (
                  <div key={ct.id} className={`order-card copy-trade-pending ${isCopyBuy ? 'buy' : 'sell'}`}>
                    {/* Card Header */}
                    <div className="order-header">
                      <div className="stock-info">
                        <h3>{ct.stock_symbol}</h3>
                        <span className="stock-name">{ct.stock_name}</span>
                      </div>
                      <span className="status-badge waiting">Awaiting Leader Fill</span>
                    </div>

                    {/* Details */}
                    <div className="order-details">
                      <div className="detail-row">
                        <span className="label">Type:</span>
                        <span className={`value order-type ${isCopyBuy ? 'buy' : 'sell'}`}>
                          Copy {ct.order_side} — {ct.order_type} Order
                        </span>
                      </div>

                      <div className="detail-row">
                        <span className="label">{isCopyBuy ? 'Reserved Amount:' : 'Order Value:'}</span>
                        <span className="value highlight bold">
                          {ct.price_display || formatPrice(ct.price)}
                        </span>
                      </div>

                      {ct.limit_price && (
                        <div className="detail-row">
                          <span className="label">Leader's Limit Price:</span>
                          <span className="value">
                            {formatPrice(ct.limit_price)}
                          </span>
                        </div>
                      )}

                      <div className="detail-row">
                        <span className="label">Created:</span>
                        <span className="value date">
                          {new Date(ct.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Explanation */}
                    <div className="order-explanation">
                      <strong>What happens next?</strong>
                      <p>
                        {isCopyBuy ? (
                          <>
                            Your funds are reserved and will be used to copy this trade
                            automatically once the leader's limit order fills. If the leader
                            cancels their order, the reserved amount will be returned to your
                            available balance.
                          </>
                        ) : (
                          <>
                            A pending sell order has been placed on your behalf and will
                            execute automatically once the leader's limit order fills. No
                            funds are reserved — your shares will be sold at the leader's
                            limit price when it is reached.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PendingOrders;